import React, { FC, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Alert from '@material-ui/lab/Alert';
import {
  closeError,
  getProduct,
  getProductFormApiErrors,
  getProductFormLoaders,
  saveProductProperties
} from './productForm';
import { array, mixed, object, string } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, get, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import DeleteIcon from '@material-ui/icons/DeleteOutlineOutlined';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import { AppDispatch } from 'store/store';
import {
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Hidden,
  Button,
  CircularProgress,
  IconButton,
  Snackbar
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { Column } from 'components/UI/Column';
import { Row } from 'components/UI/Row';
import { isEmpty, remove } from 'lodash';
import { StickyDiv } from 'components/UI/StickyDiv';

const measures = ['any', 'qty', 'mm', 'cm', 'm', 'g', 'kg', 'ml', 'l', 's', 'A', 'V', 'K', 'mol', 'cd'];

interface ProductPropertyForm {
  label: string;
  value: string;
  measure: string;
}
interface ProductPropertiesForm {
  properties: ProductPropertyForm[];
}
const PropertyFieldSchema = object().shape({
  label: string().required('products.property.name is required'),
  value: string().required('products.property.value is required'),
  measure: mixed().oneOf(measures)
});

const PropertiesSchema = object().shape({ properties: array().of(PropertyFieldSchema) });

const ProductProperties: FC<{}> = () => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const { handleSubmit, errors, setValue, control, setError, getValues } = useForm<ProductPropertiesForm>({
    mode: 'onBlur',
    resolver: yupResolver(PropertiesSchema)
  });

  const { productId } = useParams();
  const product = useSelector(getProduct);
  const { isLoading, isSaving } = useSelector(getProductFormLoaders);
  const { loadingError, savingError } = useSelector(getProductFormApiErrors);

  const { fields, append, remove, swap } = useFieldArray({ control, name: 'properties' });

  useEffect(() => {
    if (product) {
      const { properties } = product;
      setValue('properties', properties, { shouldDirty: false });
    }
  }, [product, setValue]);

  useEffect(() => {
    if (savingError) {
      setError('name', { type: 'required', message: t('products.property.update error') });
    }
  }, [savingError, setError, t]);

  const onSubmit = useCallback(
    async (formData: ProductPropertiesForm) => {
      await dispatch(saveProductProperties({ id: product!.id, properties: formData.properties }));
    },
    [dispatch, product]
  );

  if (loadingError) {
    return <Alert severity="error">{loadingError}</Alert>;
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} noValidate style={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
        {isLoading && (
          <Card variant="outlined" style={{ marginTop: '20px' }}>
            <CardContent style={{ display: 'flex', flexWrap: 'wrap' }}>
              <Skeleton variant="text" width="100%" height="290px" />
            </CardContent>
          </Card>
        )}

        {fields.map((item, index) => (
          <Card variant="outlined" key={item.id} style={{ marginTop: '20px' }}>
            <CardContent style={{ display: 'flex', flexWrap: 'wrap' }}>
              <Column flex={1}>
                <Row>
                  <Grid container spacing={2}>
                    <Grid item xs={3} sm={3} md={3} lg={3}>
                      <Controller
                        as={
                          <TextField
                            autoComplete="new-password"
                            helperText={t(get(errors, `properties[${index}].label.message`))}
                            error={!isEmpty(get(errors, `properties[${index}].label`))}
                            label={t('products.property.name')}
                            style={{ marginBottom: '16px' }}
                            fullWidth
                          />
                        }
                        control={control}
                        defaultValue={item?.label || ''}
                        name={`properties[${index}].label`}
                      />
                    </Grid>
                    <Grid item xs={7} sm={7} md={7} lg={7}>
                      <Controller
                        as={
                          <TextField
                            autoComplete="new-password"
                            helperText={t(get(errors, `properties[${index}].value.message`))}
                            error={!isEmpty(get(errors, `properties[${index}].value`))}
                            label={t('products.property.value')}
                            style={{ marginBottom: '16px' }}
                            fullWidth
                          />
                        }
                        control={control}
                        defaultValue={item?.value || ''}
                        name={`properties[${index}].value`}
                      />
                    </Grid>
                    <Grid item xs={2} sm={2} md={2} lg={2}>
                      <FormControl error={!isEmpty(get(errors, `properties[${index}].measure`))} fullWidth>
                        <InputLabel id="country-label">{t('products.property.measure.label')}</InputLabel>
                        <Controller
                          as={
                            <Select value={`properties[${index}].measure`} labelId="measure-label">
                              {measures.map(type => (
                                <MenuItem key={type} value={type}>
                                  {t(`products.property.measure.${type}`)}
                                </MenuItem>
                              ))}
                            </Select>
                          }
                          control={control}
                          defaultValue={item?.measure || ''}
                          name={`properties[${index}].measure`}
                        />
                        <FormHelperText>{t(get(errors, `properties[${index}].measure.message`))}</FormHelperText>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Row>
              </Column>
              <Hidden xsDown>
                <Row padding="10px 0 0 10px" flex={0.1}>
                  <IconButton onClick={() => remove(index)} color="secondary">
                    <DeleteIcon />
                  </IconButton>
                </Row>
              </Hidden>
              <Hidden smUp>
                <IconButton onClick={() => remove(index)} color="secondary">
                  <DeleteIcon />
                </IconButton>
              </Hidden>
            </CardContent>
          </Card>
        ))}

        {!isLoading ? (
          <Button
            disabled={isSaving || isLoading}
            variant="contained"
            color="primary"
            style={{ marginTop: '15px' }}
            onClick={() =>
              append({ key: '', description: '', measure: '', optional: false, value: '', value_type: 'string' })
            }
          >
            {t('products.property.add new')}
          </Button>
        ) : (
          <Skeleton variant="text" width="100%" height="19px" />
        )}

        <div style={{ height: '50px' }} />

        <StickyDiv bottom="0px" top="0" padding="0" marginBottom="-40px">
          <Row justifyContent="flex-end" borderTop="solid 1px lightgrey" padding="0 40px 10px">
            <Button
              disabled={isSaving || isLoading}
              variant="contained"
              color="primary"
              type="submit"
              style={{ height: '44px', width: '128px', marginTop: '10px' }}
            >
              {t('label.save')}
              {isSaving && (
                <CircularProgress
                  color="primary"
                  size={34}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: -17,
                    marginLeft: -17
                  }}
                />
              )}
            </Button>
          </Row>
        </StickyDiv>
      </form>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        key="bottom,center"
        open={!isEmpty(savingError)}
        autoHideDuration={2000}
        onClose={() => dispatch(closeError())}
      >
        <Alert severity={savingError ? 'error' : 'success'} variant="filled" onClose={() => dispatch(closeError())}>
          {t('products.property.update error')}
        </Alert>
      </Snackbar>
    </>
  );
};
export { ProductProperties };
